/**
 * WorkspaceSelector
 *
 * Dropdown at the top of the Scripts panel for switching between saved workspaces.
 * Hidden when no saved workspaces exist (keeps the default workspace invisible).
 */

import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { useScriptBrowserStore } from '../store/scriptBrowserStore';

export function WorkspaceSelector() {
  const workspaces = useScriptBrowserStore((s) => s.workspaces);
  const activeWorkspaceId = useScriptBrowserStore((s) => s.activeWorkspaceId);
  const switchWorkspace = useScriptBrowserStore((s) => s.switchWorkspace);
  const saveAsWorkspace = useScriptBrowserStore((s) => s.saveAsWorkspace);
  const deleteWorkspace = useScriptBrowserStore((s) => s.deleteWorkspace);

  const [isNaming, setIsNaming] = useState(false);
  const [newName, setNewName] = useState('');

  // Hidden when no saved workspaces and not in naming mode
  if (workspaces.length === 0 && !isNaming) return null;

  const handleSave = () => {
    const name = newName.trim();
    if (!name) return;
    saveAsWorkspace(name);
    setNewName('');
    setIsNaming(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setIsNaming(false);
      setNewName('');
    }
  };

  if (isNaming) {
    return (
      <div className="flex items-center gap-1 px-2 py-1 border-b border-border">
        <input
          type="text"
          className="flex-1 min-w-0 h-6 px-1.5 text-xs bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="Workspace name..."
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={handleSave}
          disabled={!newName.trim()}
        >
          Save
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-1 text-xs"
          onClick={() => {
            setIsNaming(false);
            setNewName('');
          }}
        >
          Cancel
        </Button>
      </div>
    );
  }

  const activeWs = workspaces.find((w) => w.id === activeWorkspaceId);

  return (
    <div className="flex items-center gap-1 px-2 py-1 border-b border-border">
      <Select
        value={activeWorkspaceId ?? '__default__'}
        onValueChange={(v) => switchWorkspace(v === '__default__' ? null : v)}
      >
        <SelectTrigger className="h-6 min-w-0 flex-1 text-xs border-0 shadow-none bg-transparent px-1 [&>svg:last-child]:hidden">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__default__" className="text-xs">
            Default
          </SelectItem>
          {workspaces.map((ws) => (
            <SelectItem key={ws.id} value={ws.id} className="text-xs">
              {ws.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 shrink-0"
        onClick={() => setIsNaming(true)}
        title="Save as workspace"
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>

      {activeWs && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 shrink-0 text-destructive hover:text-destructive"
          onClick={() => deleteWorkspace(activeWs.id)}
          title={`Delete workspace "${activeWs.name}"`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
